import React, { useState, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import Footer from './Footer.js';
import { Link } from "react-router-dom";
import { useNavigate } from 'react-router-dom';
import { useSearchParams } from "react-router-dom";
import { useSearchContext } from './SearchContext';

function Archive() {
  const { buildLink } = useSearchContext();
  const [activeTab, setActiveTab] = useState('access');
  const [showHelp, setShowHelp] = useState(false);

  const [keyword, setKeyword] = useState('');
  const [genre, setGenre] = useState('');
  const [title, setTitle] = useState('');
  const [fromYear, setFromYear] = useState('');
  const [toYear, setToYear] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [keywordSecond, setKeywordSecond] = useState('');
  const [fromYearSecond, setFromYearSecond] = useState('');
  const [toYearSecond, setToYearSecond] = useState('');
  const [sortBySecond, setSortBySecond] = useState('1');
  const [searchParams, setSearchParams] = useSearchParams();

  const handleTabClick = (tabName) => {
    setActiveTab(tabName);
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      params.set('section', tabName);
      return params;
    });
  };

  useEffect(() => {
    const section = searchParams.get('section');
    if (section && ['access', 'about', 'biography'].includes(section)) {
      setActiveTab(section);
    }
  }, [searchParams]);

  useEffect(() => {
    const anchor = searchParams.get('anchor');
    if (!anchor) return;

    const el = document.getElementById(anchor);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    return () => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    };
  }, [searchParams]);


  const handleSubmit = (event) => {
    event.preventDefault();

    if (fromYear && !toYear) {
      setError('Please enter a "to" year.');
      return;
    }
    if (!fromYear && toYear) {
      setError('Please enter a "from" year.');
      return;
    }

    if (!title && !genre && !keyword && !fromYear && !toYear) {
      setError('Please fill out at least one field: Title, Genre, Keyword, or Year(s).');
      return;
    }

    setError('');

    const entireText = keyword;

    const params = new URLSearchParams({
      title,
      genre,
      fromYear,
      toYear,
      entireText,
      smode: 'advanced',
    });

    navigate(`/searchresults?${params.toString()}`);
  };
  const handleSearch = (e) => {
    e.preventDefault();

    const queryParams = new URLSearchParams({
      keyword: keywordSecond,
      secondary_startyear: fromYearSecond,
      secondary_endyear: toYearSecond,
      sortby: sortBySecond,
    }).toString();

    navigate(`/secondarysearch?${queryParams}`);
  };
  return (
    <div className="container mt-4">
      <h1 className="page-header" style={{ fontWeight: '300', paddingBottom: '9px', borderBottom: '1px solid #eee' }}>The Archive</h1>
      <ul className="nav nav-tabs" role="tablist">
        <li className="tab-item" onClick={() => handleTabClick("access")} style={{ cursor: "pointer", userSelect: "none" }}>
          <button className={`nav-link ${activeTab === "access" ? "active" : ""}`}>
            <i className="fa-solid fa-magnifying-glass" style={{ color: activeTab === "access" ? "#bb4816" : "#000000" }}></i>{" "}
            Access the Archive
          </button>
        </li>
        <li className="tab-item" onClick={() => handleTabClick("about")} style={{ cursor: "pointer", userSelect: "none" }}>
          <button className={`nav-link ${activeTab === "about" ? "active" : ""}`}>
            <i className="fa-solid fa-folder-closed" style={{ color: activeTab === "about" ? "#bb4816" : "#000000" }}></i>{" "}
            About the Archive
          </button>
        </li>
        <li className="tab-item" onClick={() => handleTabClick("biography")} style={{ cursor: "pointer", userSelect: "none" }}>
          <button className={`nav-link ${activeTab === "biography" ? "active" : ""}`}>
            <i className="fa-solid fa-book" style={{ color: activeTab === "biography" ? "#bb4816" : "#000000" }}></i>{" "}
            Kennedy Biography
          </button>
        </li>
      </ul>

      <div className="tab-content mt-3">
        {activeTab === "access" && (
          <div className="tab-pane fade active show">
            <h2
              id="accessheading"
              className="archivetext"
              style={{ color: '#bb4816', fontWeight: '300' }}
            >
              Access the Archive
            </h2>
            <p className="archivetext">Users can access through a single portal the texts of <em>all</em> Brown’s primary works, along with a secondary bibliography.</p>
            <p className="archivetext">Users may <em>browse</em> “The Comprehensive <a href={buildLink('/bibliography')}>Primary Bibliography</a> of the Writings of Charles Brockden Brown, 1783-1822” or <em>search</em> full-text transcriptions of 992 <a href="http://www.tei-c.org/Guidelines/P5/" target="_blank" rel="noreferrer noopener">TEI P5</a>-encoded texts, which include <em>all</em> of Brown’s novels, periodical writings, letter manuscripts (facsimile), and other writings as well as writings that are designated as “hybrid” because of his use of material by other authors.</p>
            <p className="archivetext">For primary publications, an "A" at the end of a citation indicates that a work is indisputably written by Brown, while "B" indicates a high degree of confidence of Brown's authorship. Brown's periodical writing also includes a range of hybrid texts in which Brown excerpts, summarizes, and otherwise creatively edits and shapes other works while adding his own implicit or explicit commentary and contextual framing within each particular magazine issue. Such texts are marked "H" to correspond with "hybrid." Included in the bibliography are items that were<a href={buildLink('/bibliography?section=removed')}> removed </a>after they were identified as works of other authors.</p>
            <p className="archivetext">The <a href={buildLink('/bibliography?section=secondary')}>Secondary Bibliography,</a> which is not full-text, contains 1,238 items, dates back to 1796, and is regularly updated. In addition to author, date, keyword, and title searches, one may use assigned <a href={buildLink('/loc')}>Library of Congress Subject Headings</a>. Like the primary bibliography, one may <em>browse</em> and <em>search</em> secondary bibliography materials.</p>
            <p className="archivetext">
              Users of the archive can view PDF versions of{' '}
              <a
                href={buildLink('/archive/?section=biography')}
                className="control-tabs"
                onClick={(e) => {
                  e.preventDefault();
                  setSearchParams({ section: 'biography' });
                  setActiveTab('biography');
                }}
              >
                Daniel Edwards Kennedy’s massive unedited biography on Brown
              </a>.
            </p>
            <form onSubmit={handleSubmit}>
              <div className="searchcontainer" style={{ backgroundColor: '#fffeea', border: '2px solid #ddd', padding: '20px', margin: '1.5em 0' }}>
                <div className="row">
                  <div className="col-sm-3">
                    <h4>
                      <a href={buildLink('/bibliography')} style={{ fontSize: '18px' }}>
                        Primary Bibliography
                      </a>
                      <br />
                      <a href={buildLink('/browse')} style={{ fontSize: '18px' }}>
                        (and full text)
                      </a>
                      <br />
                      <br />
                      <a href={buildLink('/bibliography?section=removed')} style={{ fontSize: '18px' }}>
                        Removed Items
                      </a>
                    </h4>
                  </div>
                  <div className="col-sm-9">
                    <table>
                      <tbody>
                        <tr>
                          <td align="right">keyword</td>
                          <td>
                            <input
                              type="text"
                              name="keyword"
                              className="archive-input-size-50"
                              value={keyword}
                              onChange={(e) => setKeyword(e.target.value)}
                              style={{ marginBottom: '10px', marginLeft: '5px' }}
                            />
                          </td>
                        </tr>
                        <tr>
                          <td align="right">genre</td>
                          <td>
                            <input
                              type="text"
                              name="genre"
                              className="archive-input-size-50"
                              value={genre}
                              onChange={(e) => setGenre(e.target.value)}
                              style={{ marginBottom: '10px', marginLeft: '5px' }}
                            />
                          </td>
                        </tr>
                        <tr>
                          <td align="right">title</td>
                          <td>
                            <input
                              type="text"
                              name="title"
                              className="archive-input-size-50"
                              value={title}
                              onChange={(e) => setTitle(e.target.value)}
                              style={{ marginBottom: '5px', marginLeft: '5px' }}
                            />
                          </td>
                        </tr>
                        <tr>
                          <td align="right">from</td>
                          <td>
                            <input
                              type="text"
                              name="fromYear"
                              className="archive-input-size-15"
                              value={fromYear}
                              onChange={(e) => setFromYear(e.target.value)}
                              style={{ marginRight: '3px', marginBottom: '10px', marginLeft: '5px', marginTop: '10px' }}
                            />
                            to
                            <input
                              type="text"
                              name="toYear"
                              className="archive-input-size-15"
                              value={toYear}
                              onChange={(e) => setToYear(e.target.value)}
                              style={{ marginLeft: '5px', marginRight: '10px' }}

                            />
                            <input
                              type="submit"
                              value="Search"
                            />
                          </td>
                        </tr>
                        <tr>
                          <td colSpan="2" align="left" style={{ paddingLeft: '60px' }}>
                            <a href="#help" onClick={(e) => { e.preventDefault(); setShowHelp(true); }}>
                              Help with advanced searching
                            </a>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {error && <p className="error-message">{error}</p>}
            </form>
            <div className="searchcontainer" style={{ backgroundColor: '#fffeea', border: '2px solid #ddd', padding: '20px', margin: '1.5em 0' }}>
              <div className="row">
                <div className="col-sm-2">
                  <h4>
                    <a
                      href={buildLink('/bibliography?section=secondary')}
                      className="control-tabs"
                      style={{ fontSize: '18px' }}
                    >
                      Secondary Bibliography
                    </a>
                  </h4>
                </div>
                <div className="col-sm-10">
                  <form onSubmit={handleSearch}>
                    <label htmlFor="keyword" style={{ fontWeight: '600', marginRight: '3px', marginBottom: '5px' }}>Enter search term:</label>
                    <input
                      id="keyword"
                      type="text"
                      name="keyword"
                      className="archive-input-size-40"
                      placeholder="title, keyword, subject, author, or editor"
                      value={keywordSecond}
                      onChange={(e) => setKeywordSecond(e.target.value)}
                    />
                    <strong style={{ fontWeight: '600', marginRight: '3px', marginLeft: '3px' }}>From</strong>
                    <input
                      type="text"
                      name="secondary_startyear"
                      placeholder="year"
                      size="9"
                      value={fromYearSecond}
                      onChange={(e) => setFromYearSecond(e.target.value)}
                    />
                    <strong style={{ fontWeight: '600', marginRight: '3px', marginLeft: '3px' }}>to</strong>
                    <input
                      type="text"
                      name="secondary_endyear"
                      placeholder="year"
                      size="9"
                      value={toYearSecond}
                      onChange={(e) => setToYearSecond(e.target.value)}
                    /><br />

                    <strong style={{ fontWeight: '600', marginRight: '3px', marginLeft: '3px' }}>Order by</strong>
                    <input
                      name="sortby"
                      type="radio"
                      value="1"
                      checked={sortBySecond === '1'}
                      onChange={() => setSortBySecond('1')}
                    />
                    date &nbsp;&nbsp;
                    <input
                      name="sortby"
                      type="radio"
                      value="2"
                      checked={sortBySecond === '2'}
                      onChange={() => setSortBySecond('2')}
                    />
                    name
                    &nbsp;&nbsp;
                    <input type="submit" value="Search" />
                  </form>
                </div>
              </div>
            </div>

            <div
              className="searchcontainer"
              style={{
                backgroundColor: '#fffeea',
                border: '2px solid #ddd',
                padding: '20px',
                margin: '1.5em 0',
              }}
            >
              <div className="row align-items-center">
                <div className="col-sm-3">
                  <h4>
                    <Link to="/loc" className="control-tabs" style={{ fontSize: '18px' }}>
                      Library of Congress<br />Subject Headings
                    </Link>
                  </h4>
                </div>
                <div className="col-sm-9">
                  <Link to="/loc" className="btn btn-default btn-s">
                    Selected Library of Congress Subject Headings to enhance your search for scholarship on Brown
                  </Link>
                </div>
              </div>
            </div>

            <p>The Charles  Brockden Brown Electronic Archive and Scholarly Edition is affiliated with the <a href="https://nines.org/">NINES</a> (Networked  Infrastructure for Nineteenth-Century Electronic Scholarship) and <a href="http://www.18thconnect.org/">18thConnect</a>. The primary and secondary bibliographic information is available through <a href="https://nines.org/search?q=brockden%20brown" target="_blank" rel="noopener noreferrer">NINES</a>.</p>
            <p>For the Charles Brockden Brown Electronic Archive and Scholarly Edition protocols see: <a href="https://brockdenbrown.cah.ucf.edu/files/CBBArchiveProtocols20160421.pdf" target="_blank" rel="noopener noreferrer">2016 Protocols</a></p>
            <p>For a critical description of archival processes and procedures, see “<a href="http://www.archivejournal.net/issue/4/archives-remixed/the-charles-brockden-brown-electronic-archive-mapping-archival-access-and-metadata/" target="_blank" rel="noopener noreferrer">The Charles Brockden Brown Electronic Archive: Mapping Archival Access and Metadata</a>.” <em>Archive Journal</em> 4 (Spring 2014) <a href="http://www.archivejournal.net/issue/4/archives-remixed/" target="_blank" rel="noopener noreferrer">http://www.archivejournal.net/issue/4/archives-remixed/</a>. Mark L. Kamrath, Philip Barnard, Rudy McDaniel, William Dorner, Kevin Jardaneh, Patricia Carlton, Josejuan Rodriguez.</p>
            <p>For access to  TEI markup files, please see each individual text.</p>
            <p><a href="mailto:mark.kamrath@ucf.edu; philipb@ku.edu">Contact the editors</a> regarding the archive's contents.</p>
          </div>
        )}
        {activeTab === "about" && (
          <div className="tab-pane fade active show">
            <h2>About the Archive</h2>
            <p>This archive uses <a href="http://xtf.cdlib.org/" target="_blank" rel="noreferrer noopener">XTF</a> to index bibliographical and primary source document materials.</p>
            <p>The bibliographical materials consist of Brown's primary works along with several thousand secondary citations that are regularly updated. However, there are various degrees of certainty associated with Brown's texts in the archive, and the number of texts in the database is subject to some fluctuation as textual authorship is determined. We currently designate each text as either “A” (Brown's authorship is certain), “B” (Brown's authorship is likely but not certain), or “H” (Brown’s work is conflated with that of other people), based on the weight of external and internal textual evidence.</p>
            <p>To date, 992 <a href="https://tei-c.org/Guidelines/P5/" target="_blank" rel="noreferrer noopener">TEI P5</a>-encoded full-text primary source documents have been transcribed, coded, and proofed against the originals with a high degree of accuracy. This includes Brown's novels, periodical writings, letter manuscripts (facsimile), and other publications. All of his writings are now available through a single portal.</p>
            <p>The TEI encoding of these texts is also available to users. Our metadata has undergone peer-review and is also affiliated with <a href="https://nines.org/">NINES (Networked Infrastructure for Nineteenth-Century Electronic Scholarship)</a> and <a href="https://18thconnect.org/">18thCentury Connect</a>.</p>
            <p>The production of seven MLA-CSE approved print volumes of material represents a subset of the archive, and provides historical and textual essays for each volume, extensive annotation, and other apparatus. The volumes are being published by Bucknell University Press.</p>
          </div>
        )}
        {activeTab === "biography" && (
          <div className="tab-pane fade active show">
            <h2>Kennedy Biography, c. 1948</h2>
            <p>Users of the archive can access Daniel Edwards Kennedy’s unedited biography on Brown, courtesy of the <a href="https://www.library.kent.edu/special-collections-and-archives/charles-brockden-brown-bicentennial-edition-records">Kent State University Institute for Bibliography and Editing</a>.</p>
            <p>Using the <a href="/brockdenbrown/files/KennedyBio_TOC.pdf" target="_blank" rel="noreferrer noopener">Table of Contents</a> and <a href="/brockdenbrown/files/KennedyBio_Index.pdf" target="_blank" rel="noreferrer noopener">Index</a> to determine which sections to download.</p>
            <div style={{ marginLeft: '50px' }}>
              <a href="/brockdenbrown/files/KennedyBio_Front-BackMatter.zip">Front/Backmatter</a> (2MB)<br />
              <a href="/brockdenbrown/files/KennedyBio_01-10.zip">Sections 1-10</a> (107MB)<br />
              <a href="/brockdenbrown/files/KennedyBio_11-20.zip">Sections 11-20</a> (91MB)<br />
              <a href="/brockdenbrown/files/KennedyBio_21-30.zip">Sections 21-30</a> (70MB)<br />
              <a href="/brockdenbrown/files/KennedyBio_31-40.zip">Sections 31-40</a> (22MB)<br />
              <a href="/brockdenbrown/files/KennedyBio_41-49.zip">Sections 41-49</a> (24MB)
            </div>
            <blockquote>
              <small>
                <em>*Special note:</em> On November 30, 2005, Special Collections at the University of Virginia, based on legal concerns, removed from its collection photographs of a number of manuscript Brown documents that it had received years earlier from manuscript dealers. The University of Virginia never owned the documents, but possessed photographs that dealers had used to interest the library in purchasing them. These documents have passed into private collections where they are unlocatable and the photographs were thus the only public and locatable record of them. While some of these photographs reproduce items available elsewhere (e.g.., in the Kennedy typescript biography), others are of items otherwise unavailable and effectively lost. In our archive, we have therefore preserved reproductions made from photocopies that document these items which, prior to 2005, were part of the Charles Brockden Brown collection at the Clifton Walker Barrett Library. For more information, please see the <a href={buildLink('/uva-collection')}>University of Virginia collection page</a>, containing the removal notice, the pre-2005 catalog list, and 38 scanned reproductions from the collection.
              </small>
            </blockquote>

          </div>
        )}
      </div>

      <Modal show={showHelp} onHide={() => setShowHelp(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Help with advanced searching</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <table>
            <thead>
              <tr>
                <th>Search Query</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>south america</td>
                <td>Search keywords for 'south' AND 'america'</td>
              </tr>
              <tr>
                <td>"south america"</td>
                <td>Search keywords for the exact phrase 'south america'</td>
              </tr>
              <tr>
                <td>"south america" AND 1805</td>
                <td>Search keywords for the exact phrase 'south america' AND 1805</td>
              </tr>
              <tr>
                <td>"south america" OR "north america"</td>
                <td>Search keywords for the exact phrase 'south america' OR the exact phrase 'north america'</td>
              </tr>
              <tr>
                <td>(south OR north) america</td>
                <td>Search keywords for either 'south' OR 'north' AND 'america'</td>
              </tr>
              <tr>
                <td>title:political NOT character</td>
                <td>Search all titles for the word 'political' but NOT the word 'character'</td>
              </tr>
            </tbody>
          </table>
          <p>Operator substitutions:</p>
          <ul>
            <li><strong>&</strong>: AND</li>
            <li><strong>|</strong>: OR</li>
            <li><strong>-</strong>: NOT</li>
          </ul>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowHelp(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>





      <Footer />


    </div>

  );
}

export default Archive;
